import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

const indicator = (tooltipText, { hasErrors, hasWarnings }) => (
  <div
    className={classnames({
      'with-indicator': true,
      'with-indicator--warning': !hasErrors & hasWarnings,
    })}
    data-tip={tooltipText}
  />
);

const defaultCellRenderer = ({ value, valueFormatted }) => valueFormatted || value;

const getTooltipText = ({
  withDefaultValueText,
  validationErrorTooltipText,
  validationWarningsTooltipText,
}) => [
  // eslint-disable-next-line no-undefined
  ![undefined, ''].includes(withDefaultValueText) ? `Default value: ${withDefaultValueText}` : '',
  validationErrorTooltipText ? `Validation Errors: ${validationErrorTooltipText}` : '',
  validationWarningsTooltipText ? `Validation Warnings: ${validationWarningsTooltipText}` : '',
].filter(Boolean).join(' ');

export const IndicatorAndCustomInnerRendererCellRenderer = (customInnerRenderer = defaultCellRenderer) => (
  function WithIndicatorCellRenderer(cellData) {
    const { data, colDef } = cellData;
    const { validationMessages, touched, forceShowErrors } = data && data.meta ? data.meta : {};

    const validationErrorTooltipText = validationMessages?.errors?.[colDef.field]
      && (forceShowErrors || (touched && touched[colDef.field]))
      ? validationMessages.errors[colDef.field].join(', ')
      : '';

    const validationWarningsTooltipText = validationMessages?.warnings?.[colDef.field]?.join(', ') ?? '';

    const { overrideConfig } = colDef.customExport || {};

    const source = overrideConfig && data[overrideConfig.sourceField];
    const value = overrideConfig && data[overrideConfig.valueField];

    const hasOverride = overrideConfig && overrideConfig.comparator
      ? !overrideConfig.comparator(source, value)
      : source !== value;

    const formattedSource = overrideConfig && overrideConfig.formatter
      ? overrideConfig.formatter(source)
      : source;

    const withDefaultValueText = hasOverride ? formattedSource : '';

    const tooltipText = getTooltipText({
      withDefaultValueText, validationErrorTooltipText, validationWarningsTooltipText,
    });

    return (
      <div>
        {customInnerRenderer(cellData)}
        {tooltipText
          ? indicator(tooltipText, {
            hasErrors: Boolean(validationErrorTooltipText),
            hasWarnings: Boolean(validationWarningsTooltipText),
          })
          : null
        }
      </div>
    );
  }
);

IndicatorAndCustomInnerRendererCellRenderer.propTypes = {
  cellData: PropTypes.shape({
    value: PropTypes.shape({
      value: PropTypes.string,
      isUpdated: PropTypes.bool,
    }),
    data: PropTypes.shape({
      validationData: PropTypes.shape({
        value: PropTypes.objectOf(PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
          PropTypes.bool,
        ])),
      }),
    }),
    colDef: PropTypes.shape({
      field: PropTypes.string,
    }),
  }),
};

export const withIndicatorAndCustomInnerRendererCellRenderer = columnDefinition => ({
  ...columnDefinition,
  cellRenderer: IndicatorAndCustomInnerRendererCellRenderer(columnDefinition.cellRenderer),
});

const appendCustomRenderer = columnDefs => columnDefs.map(withIndicatorAndCustomInnerRendererCellRenderer);

export default appendCustomRenderer;
